import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, Mail, Lock, User, Building2, Shield, ArrowRight, Sparkles, Check, Phone, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useDispatch } from "react-redux";
import { setUser } from "../redux/authSlice";
import { BASE_API_URL } from "../data/api";

const EMAIL_REGEX = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

// Animated Background
function AnimatedBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900" />
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          rotate: [360, 180, 0],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
      />
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: [0, 1, 0], y: [100, -100] }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
          className="absolute w-2 h-2 bg-white/20 rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
          }}
        />
      ))}
    </div>
  );
}

// Role Selection Card
function RoleCard({ icon: Icon, title, description, selected, onClick, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.4 }}
      onClick={onClick}
      whileHover={{ scale: 1.03, y: -3 }}
      whileTap={{ scale: 0.97 }}
      className={`relative p-5 rounded-2xl cursor-pointer transition-all duration-300 overflow-hidden ${
        selected
          ? "bg-gradient-to-br from-indigo-600 to-purple-600 shadow-lg shadow-indigo-500/30 ring-2 ring-indigo-400"
          : "bg-white/10 backdrop-blur-lg border border-white/20 hover:bg-white/15 hover:border-white/30"
      }`}
    >
      <div className="flex items-center gap-4">
        <div
          className={`w-14 h-14 rounded-xl flex items-center justify-center ${
            selected ? "bg-white/20" : "bg-gradient-to-br from-indigo-500/40 to-purple-500/40"
          }`}
        >
          <Icon className={`w-7 h-7 ${selected ? "text-white" : "text-indigo-200"}`} />
        </div>
        <div className="flex-1">
          <h3 className={`font-semibold text-lg ${selected ? "text-white" : "text-white"}`}>{title}</h3>
          <p className={`text-sm ${selected ? "text-white/80" : "text-white/50"}`}>{description}</p>
        </div>
        {selected && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-7 h-7 rounded-full bg-white flex items-center justify-center">
            <Check className="w-4 h-4 text-indigo-600" />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// Input Field Component
function LoginInput({ icon: Icon, type, placeholder, value, onChange, error, showPasswordToggle, delay }) {
  const [focused, setFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputType = showPasswordToggle && showPassword ? "text" : type;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="relative"
    >
      <motion.div
        className={`relative rounded-xl overflow-hidden transition-all duration-300 ${
          error ? "ring-2 ring-red-500/50" : "ring-1"
        }`}
        style={{
          borderColor: error ? "rgba(239, 68, 68, 0.5)" : "rgba(255, 255, 255, 0.1)",
          boxShadow: focused ? "0 0 25px rgba(99, 102, 241, 0.2)" : "none",
        }}
      >
        <div
          className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors z-10 ${
            focused ? "text-indigo-400" : "text-white/40"
          }`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <input
          type={inputType}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="w-full bg-white/5 py-4 pl-12 pr-12 text-white placeholder-white/40 outline-none transition-colors"
        />
        {showPasswordToggle && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}
      </motion.div>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-red-400 text-sm mt-2 flex items-center gap-2"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
          {error}
        </motion.p>
      )}
    </motion.div>
  );
}

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isRegister, setIsRegister] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", phone: "", password: "", type: "" });
  const [errors, setErrors] = useState({});
  const [regErrors, setRegErrors] = useState({});

  const roles = [
    { id: "Customer", icon: User, title: "Customer", description: "Browse & purchase products" },
    { id: "Vendor", icon: Building2, title: "Vendor", description: "Sell & manage products" },
    { id: "Admin", icon: Shield, title: "Admin", description: "Manage platform & users" },
  ];

  const validateLogin = () => {
    const e = {};
    if (!EMAIL_REGEX.test(loginForm.email)) {
      e.email = "Please enter a valid email address";
    }
    if (!loginForm.password || loginForm.password.length < 6) {
      e.password = "Password must be at least 6 characters";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateRegister = () => {
    const e = {};
    if (!regForm.name || regForm.name.length < 3) {
      e.name = "Name must be at least 3 characters";
    }
    if (!EMAIL_REGEX.test(regForm.email)) {
      e.email = "Please enter a valid email address";
    }
    if (!regForm.phone || regForm.phone.length < 10) {
      e.phone = "Please enter a valid phone number";
    }
    if (!regForm.password || regForm.password.length < 6) {
      e.password = "Password must be at least 6 characters";
    }
    setRegErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!selectedRole) {
      toast.error("Please select your account type", {
        style: { background: "#1e293b", color: "#fff" },
      });
      return;
    }

    if (!validateLogin()) return;

    setIsLoading(true);

    try {
      const endpoint =
        selectedRole === "Admin"
          ? `${BASE_API_URL}/Admin/login`
          : `${BASE_API_URL}/Profile/login`;

      const payload =
        selectedRole === "Admin"
          ? { Email: loginForm.email, Password: loginForm.password }
          : { Email: loginForm.email, Password: loginForm.password, Type: selectedRole };

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Login failed");
      }

      localStorage.setItem("user", JSON.stringify(data.user || data.admin));
      localStorage.setItem("role", selectedRole);

      if (selectedRole === "Vendor" && data.user?.userId) {
        localStorage.setItem("vendorId", data.user.userId);
      }

      dispatch(setUser(data.user || data.admin));

      toast.success(data.message || `Welcome back, ${selectedRole}!`, {
        style: { background: "#1e293b", color: "#fff" },
        iconTheme: { primary: "#6366f1", secondary: "#fff" },
      });

      setTimeout(() => {
        if (selectedRole === "Admin") {
          navigate("/admin_dashboard");
        } else if (selectedRole === "Vendor") {
          navigate("/____seller_dashboard");
        } else {
          navigate("/dashboard");
        }
      }, 1500);
    } catch (error) {
      toast.error(error.message || "Something went wrong. Please try again.", {
        style: { background: "#1e293b", color: "#fff" },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!selectedRole) {
      toast.error("Please select your account type", {
        style: { background: "#1e293b", color: "#fff" },
      });
      return;
    }

    if (!validateRegister()) return;

    setIsLoading(true);

    try {
      const payload = {
        fullName: regForm.name,
        email: regForm.email,
        phone: regForm.phone,
        password: regForm.password,
        type: selectedRole,
      };

      const res = await fetch(`${BASE_API_URL}/Profile/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Registration failed");
      }

      toast.success("Account created successfully! Please login.", {
        style: { background: "#1e293b", color: "#fff" },
      });

      setTimeout(() => {
        setIsRegister(false);
        setLoginForm({ email: regForm.email, password: "" });
      }, 1500);
    } catch (error) {
      toast.error(error.message || "Something went wrong. Please try again.", {
        style: { background: "#1e293b", color: "#fff" },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setErrors({});
    setRegErrors({});
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <AnimatedBackground />

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="bg-slate-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

          <div className="p-8 pb-6 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg shadow-indigo-500/30"
            >
              <Sparkles className="w-8 h-8 text-white" />
            </motion.div>
            <h2 className="text-2xl font-bold text-white">
              {isRegister ? "Create Account" : "Welcome Back"}
            </h2>
            <p className="text-white/50 mt-2">
              {isRegister ? "Join our community today" : "Sign in to continue"}
            </p>
          </div>

          <div className="p-8 pt-0">
            <AnimatePresence mode="wait">
              {!selectedRole ? (
                <motion.div
                  key="role-selection"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-3"
                >
                  <p className="text-white/70 text-sm font-medium mb-2">
                    {isRegister ? "Select account type:" : "Select your account type:"}
                  </p>
                  {roles.map((role, index) => (
                    <RoleCard
                      key={role.id}
                      icon={role.icon}
                      title={role.title}
                      description={role.description}
                      selected={selectedRole === role.id}
                      onClick={() => handleRoleSelect(role.id)}
                      delay={0.1 * (index + 1)}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setSelectedRole("")}
                      className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                      <ArrowRight className="w-5 h-5 text-white rotate-180" />
                    </motion.button>
                    <div>
                      <span className="text-white/50 text-sm">
                        {isRegister ? "Creating account as" : "Signing in as"}
                      </span>
                      <p className="text-indigo-400 font-semibold text-lg">{selectedRole}</p>
                    </div>
                  </div>

                  {isRegister ? (
                    <form onSubmit={handleRegister} className="space-y-4">
                      <LoginInput
                        icon={User}
                        type="text"
                        placeholder="Full Name"
                        value={regForm.name}
                        onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                        error={regErrors.name}
                        delay={0.1}
                      />
                      <LoginInput
                        icon={Mail}
                        type="email"
                        placeholder="Email address"
                        value={regForm.email}
                        onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                        error={regErrors.email}
                        delay={0.2}
                      />
                      <LoginInput
                        icon={Phone}
                        type="tel"
                        placeholder="Phone Number"
                        value={regForm.phone}
                        onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })}
                        error={regErrors.phone}
                        delay={0.3}
                      />
                      <LoginInput
                        icon={Lock}
                        type="password"
                        placeholder="Password"
                        value={regForm.password}
                        onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                        error={regErrors.password}
                        showPasswordToggle
                        delay={0.4}
                      />

                      <motion.button
                        type="submit"
                        disabled={isLoading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full py-4 mt-6 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                          />
                        ) : (
                          <>
                            <UserPlus className="w-5 h-5" />
                            Create Account
                          </>
                        )}
                      </motion.button>

                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="text-center text-white/50 mt-6"
                      >
                        Already have an account?{" "}
                        <button
                          type="button"
                          onClick={() => { setIsRegister(false); setRegForm({ name: "", email: "", phone: "", password: "", type: "" }); }}
                          className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
                        >
                          Sign In
                        </button>
                      </motion.p>
                    </form>
                  ) : (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <LoginInput
                        icon={Mail}
                        type="email"
                        placeholder="Email address"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        error={errors.email}
                        delay={0.1}
                      />
                      <LoginInput
                        icon={Lock}
                        type="password"
                        placeholder="Password"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        error={errors.password}
                        showPasswordToggle
                        delay={0.2}
                      />

                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="flex justify-end"
                      >
                        <button
                          type="button"
                          className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                          onClick={() => toast("Reset password functionality coming soon!", { icon: "🔐" })}
                        >
                          Forgot password?
                        </button>
                      </motion.div>

                      <motion.button
                        type="submit"
                        disabled={isLoading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full py-4 mt-6 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                          />
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5" />
                            Sign In
                          </>
                        )}
                      </motion.button>

                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="text-center text-white/50 mt-6"
                      >
                        Don't have an account?{" "}
                        <button
                          type="button"
                          onClick={() => { setIsRegister(true); setLoginForm({ email: "", password: "" }); }}
                          className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
                        >
                          Create one
                        </button>
                      </motion.p>
                    </form>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-white/30 text-sm mt-6"
        >
          © 2024 WVT Elevat0or. All rights reserved.
        </motion.p>
      </motion.div>
    </div>
  );
};

export default LoginPage;
